import {
    Point,
    Position,
    Edge,
    Orientation,
    Direction,
    Outside,
    isOutside
} from './GridGeometry.js';

const DEFAULT_GRID_SIZE = 10;

export type { Point, Position, Edge };

/** Configuration for the detector */
export interface GridHitConfig {
    cellSize: number;
    padding: number;
    gridSize?: number;
}

/** Combined hit result for multi-type detection */
export type GridHit =
    | { type: 'cell'; hit: Position }
    | { type: 'edge'; hit: Edge }
    | { type: 'intersection'; hit: Point }
    | { type: 'none' };

export class GridHitDetector {
    private readonly cellSize: number;
    private readonly padding: number;
    private readonly gridSize: number;

    constructor(config: GridHitConfig) {
        this.cellSize = config.cellSize;
        this.padding = config.padding;
        this.gridSize = config.gridSize ?? DEFAULT_GRID_SIZE;
    }

    /** Normalize point order for edge (by col then row) */
    private normalizePointPair(a: Point, b: Point): [Point, Point] {
        if (a.col < b.col || (a.col === b.col && a.row < b.row)) {
            return [a, b];
        }
        return [b, a];
    }

    /** Create edge between two adjacent points (size-aware). */
    private createEdge(p1: Point, p2: Point): Edge | null {
        const rowDiff = Math.abs(p1.row - p2.row);
        const colDiff = Math.abs(p1.col - p2.col);

        if (!((rowDiff === 1 && colDiff === 0) || (rowDiff === 0 && colDiff === 1))) {
            return null;
        }

        const [pa, pb] = this.normalizePointPair(p1, p2);
        const orient: Orientation = colDiff === 0 ? 'v' : 'h';

        let ca: Position | Outside;
        let cb: Position | Outside;

        if (orient === 'v') {
            const leftCol = Math.min(pa.col, pb.col) - 1;
            const rightCol = leftCol + 1;
            const row = pa.row;

            ca = leftCol < 0
                ? { row, col: -1, side: 'W' as Direction }
                : { row, col: leftCol };
            cb = rightCol >= this.gridSize
                ? { row, col: this.gridSize, side: 'E' as Direction }
                : { row, col: rightCol };
        } else {
            const topRow = Math.min(pa.row, pb.row) - 1;
            const bottomRow = topRow + 1;
            const col = pa.col;

            ca = topRow < 0
                ? { row: -1, col, side: 'N' as Direction }
                : { row: topRow, col };
            cb = bottomRow >= this.gridSize
                ? { row: this.gridSize, col, side: 'S' as Direction }
                : { row: bottomRow, col };
        }

        return { pa, pb, ca, cb, orient };
    }

    /** Check if edge is on the rim (grid boundary) */
    private isRimEdge(edge: Edge): boolean {
        return isOutside(edge.ca) || isOutside(edge.cb);
    }

    /**
     * Detect cell from pixel coordinates.
     * Uses Math.floor to find which cell contains the point.
     */
    detectCell(x: number, y: number): Position | null {
        const col = Math.floor((x - this.padding) / this.cellSize);
        const row = Math.floor((y - this.padding) / this.cellSize);

        if (row < 0 || row >= this.gridSize || col < 0 || col >= this.gridSize) {
            return null;
        }

        return { row, col };
    }

    /**
     * Detect edge (between cells) from pixel coordinates.
     * Uses Math.round to snap to nearest grid line.
     * @param threshold - fractional distance from edge to count as "near" (default 0.25)
     * @param includeRim - if true, also detect rim edges (grid boundary edges)
     */
    detectEdge(x: number, y: number, threshold: number = 0.25, includeRim: boolean = false): Edge | null {
        const colExact = (x - this.padding) / this.cellSize;
        const rowExact = (y - this.padding) / this.cellSize;

        // Round to nearest grid lines
        const hLine = Math.round(rowExact);
        const vLine = Math.round(colExact);

        // Calculate distances to nearest edges
        const hDist = Math.abs(rowExact - hLine);
        const vDist = Math.abs(colExact - vLine);

        // Determine orientation (must be near one edge but not in corner)
        const nearH = hDist < threshold;
        const nearV = vDist < threshold;

        if (nearH === nearV) return null; // Both or neither (corner or center)

        let pa: Point, pb: Point;
        if (nearH) {
            // Horizontal edge at row hLine
            const col = Math.floor(colExact);
            if (col < 0 || col >= this.gridSize) return null;
            pa = { row: hLine, col };
            pb = { row: hLine, col: col + 1 };
        } else {
            // Vertical edge at col vLine
            const row = Math.floor(rowExact);
            if (row < 0 || row >= this.gridSize) return null;
            pa = { row, col: vLine };
            pb = { row: row + 1, col: vLine };
        }

        const edge = this.createEdge(pa, pb);
        if (!edge) return null;

        // Check rim edges
        if (this.isRimEdge(edge) && !includeRim) return null;

        return edge;
    }

    /**
     * Detect intersection (grid corner) from pixel coordinates.
     * Uses Math.round to snap to nearest intersection.
     * @param threshold - fractional distance to count as "near" (default 0.3)
     */
    detectIntersection(x: number, y: number, threshold: number = 0.3): Point | null {
        const col = Math.round((x - this.padding) / this.cellSize);
        const row = Math.round((y - this.padding) / this.cellSize);

        // Calculate distance to snapped point
        const snapX = this.padding + col * this.cellSize;
        const snapY = this.padding + row * this.cellSize;
        const dist = Math.sqrt((x - snapX) ** 2 + (y - snapY) ** 2);

        if (dist > this.cellSize * threshold) return null;
        if (row < 0 || row > this.gridSize) return null;
        if (col < 0 || col > this.gridSize) return null;

        return { row, col };
    }

    /**
     * Detect with priority: edge -> intersection -> cell.
     * Useful for games that need all three detection types.
     */
    detect(x: number, y: number): GridHit {
        const edge = this.detectEdge(x, y);
        if (edge) return { type: 'edge', hit: edge };

        const intersection = this.detectIntersection(x, y);
        if (intersection) return { type: 'intersection', hit: intersection };

        const cell = this.detectCell(x, y);
        if (cell) return { type: 'cell', hit: cell };

        return { type: 'none' };
    }

    /**
     * Check if click is near cell center.
     * Useful for Walls-style center-vs-edge detection.
     * @param distanceThreshold - fractional distance from center (default 0.3)
     */
    isNearCellCenter(x: number, y: number, distanceThreshold: number = 0.3): Position | null {
        const cell = this.detectCell(x, y);
        if (!cell) return null;

        const centerX = this.padding + (cell.col + 0.5) * this.cellSize;
        const centerY = this.padding + (cell.row + 0.5) * this.cellSize;
        const dist = Math.sqrt((x - centerX) ** 2 + (y - centerY) ** 2);

        if (dist < this.cellSize * distanceThreshold) {
            return cell;
        }
        return null;
    }

    getCellSize(): number {
        return this.cellSize;
    }

    getPadding(): number {
        return this.padding;
    }

    getGridSize(): number {
        return this.gridSize;
    }
}
